import { useState, useEffect } from 'react';
import { ProgressBar } from '@app/components/shared/ProgressBar';
import { CustomDropdown } from '@app/components/shared/CustomDropdown';
import { OCCASION_OPTIONS, RELATIONSHIP_OPTIONS, MAX_RECIPIENTS, UNIQUE_OCCASIONS } from '@config/formOptions';
import { getMinDate, isValidFutureDate, MIN_DATE_WEEKS_IN_FUTURE } from '@/utils/dateConstraints';
import { getNextOccasionDate, hasKnownDate } from '@/utils/occasionDates';
import { step1CircularPhoto } from '@/assets';

interface Event {
  occasion: string;
  customOccasion?: string;
  date: string;
}

interface Step1Props {
  onNext: (data: any) => void;
  flowType: 'one-off' | 'subscription';
  stepData?: any;
}

export interface Recipient {
  name: string;
  relationship: string;
  customRelationship?: string;
  budgetMin: number;
  budgetMax: number;
}

export function Step1({ onNext, flowType, stepData }: Step1Props) {
  // State for one-off flow (multiple recipients, one event)
  const [occasion, setOccasion] = useState(stepData?.step1?.occasion || '');
  const [customOccasion, setCustomOccasion] = useState(stepData?.step1?.customOccasion || '');
  const [date, setDate] = useState(stepData?.step1?.date || '');
  const [recipients, setRecipients] = useState<Recipient[]>(
    stepData?.step1?.recipients || [{ name: '', relationship: '', budgetMin: 50, budgetMax: 0 }]
  );

  // State for subscription flow (one recipient, multiple events)
  const [recipient, setRecipient] = useState(stepData?.step1?.recipient || { name: '', relationship: '' });
  const [events, setEvents] = useState<Event[]>(
    stepData?.step1?.events || [
      { occasion: '', date: '' },
      { occasion: '', date: '' },
      { occasion: '', date: '' },
      { occasion: '', date: '' },
      { occasion: '', date: '' },
      { occasion: '', date: '' },
    ]
  );

  // Track validation errors - following Step2/Step4 pattern
  const [showValidationErrors, setShowValidationErrors] = useState(false);
  const [fieldErrors, setFieldErrors] = useState<{[key: string]: string}>({});

  // Update state when stepData changes (for data persistence)
  useEffect(() => {
    if (stepData?.step1) {
      if (flowType === 'one-off') {
        setOccasion(stepData.step1.occasion || '');
        setCustomOccasion(stepData.step1.customOccasion || '');
        setDate(stepData.step1.date || '');
        setRecipients(stepData.step1.recipients || [{ name: '', relationship: '', budgetMin: 50, budgetMax: 0 }]);
      } else {
        setRecipient(stepData.step1.recipient || { name: '', relationship: '' });
        setEvents(
          stepData.step1.events || [
            { occasion: '', date: '' },
            { occasion: '', date: '' },
            { occasion: '', date: '' },
            { occasion: '', date: '' },
            { occasion: '', date: '' },
            { occasion: '', date: '' },
          ]
        );
      }
    }
  }, [stepData, flowType]);

  // Get available occasions for a specific event dropdown, excluding unique occasions already selected
  const getAvailableOccasionsForEvent = (currentEventIndex: number) => {
    if (flowType !== 'subscription') {
      return OCCASION_OPTIONS;
    }

    // Get all occasions selected in other events
    const selectedUniqueOccasions = events
      .map((event, index) => {
        // Don't consider the current event being edited
        if (index === currentEventIndex) return null;
        // Only consider unique occasions that are actually selected
        return UNIQUE_OCCASIONS.includes(event.occasion) ? event.occasion : null;
      })
      .filter(Boolean);

    // Filter out unique occasions that are already selected
    return OCCASION_OPTIONS.filter(option => {
      // If it's not a unique occasion, always allow it
      if (!UNIQUE_OCCASIONS.includes(option)) {
        return true;
      }
      // If it's a unique occasion, only allow it if not already selected
      return !selectedUniqueOccasions.includes(option);
    });
  };

  const handleEventChange = (index: number, field: keyof Event, value: string) => {
    const updated = events.map((event, i) => {
      if (i === index) {
        const updatedEvent = { ...event, [field]: value };

        // Auto-fill date when occasion is selected and has a known date
        if (field === 'occasion' && hasKnownDate(value)) {
          const nextDate = getNextOccasionDate(value);
          if (nextDate) {
            updatedEvent.date = nextDate;
          }
        }

        return updatedEvent;
      }
      return event;
    });
    setEvents(updated);
  };

  // Validation function following Step2/Step4 pattern
  const validateForm = (showErrors = false) => {
    const errors: {[key: string]: string} = {};
    let isValid = true;

    if (flowType === 'one-off') {
      // Validate occasion
      if (!occasion.trim()) {
        errors['occasion'] = 'Please select an occasion';
        isValid = false;
      }

      // Validate custom occasion if needed
      if ((occasion === 'Other' || occasion === 'Holiday') && !customOccasion.trim()) {
        errors['customOccasion'] = occasion === 'Holiday' ? 'Please specify the holiday' : 'Please specify the occasion';
        isValid = false;
      }

      // Validate date
      if (!date) {
        errors['date'] = 'Date is required';
        isValid = false;
      }

      // Validate recipients
      recipients.forEach((recipientItem, index) => {
        if (!recipientItem.name.trim()) {
          errors[`recipient-${index}-name`] = 'Recipient name is required';
          isValid = false;
        }

        if (!recipientItem.relationship) {
          errors[`recipient-${index}-relationship`] = 'Relationship is required';
          isValid = false;
        }

        if (recipientItem.relationship === 'Other' && !recipientItem.customRelationship?.trim()) {
          errors[`recipient-${index}-customRelationship`] = 'Please specify the relationship';
          isValid = false;
        }

        if (recipientItem.budgetMin <= 0) {
          errors[`recipient-${index}-budgetMin`] = 'Minimum budget must be greater than $0';
          isValid = false;
        }

        if (recipientItem.budgetMax <= 0) {
          errors[`recipient-${index}-budgetMax`] = 'Maximum budget must be greater than $0';
          isValid = false;
        }

        if (recipientItem.budgetMin > recipientItem.budgetMax) {
          errors[`recipient-${index}-budgetMax`] = 'Maximum budget must be greater than minimum budget';
          isValid = false;
        }
      });
    } else {
      // Subscription flow validation
      if (!recipient.name.trim()) {
        errors['recipient-name'] = 'Recipient name is required';
        isValid = false;
      }

      if (!recipient.relationship) {
        errors['recipient-relationship'] = 'Relationship is required';
        isValid = false;
      }

      if (recipient.relationship === 'Other' && !recipient.customRelationship?.trim()) {
        errors['recipient-customRelationship'] = 'Please specify the relationship';
        isValid = false;
      }

      // Validate events
      events.forEach((event, index) => {
        if (!event.occasion) {
          errors[`event-${index}-occasion`] = 'Please select an occasion';
          isValid = false;
        }

        if (!event.date) {
          errors[`event-${index}-date`] = 'Date is required';
          isValid = false;
        }

        if ((event.occasion === 'Other' || event.occasion === 'Holiday') && !event.customOccasion?.trim()) {
          errors[`event-${index}-customOccasion`] = event.occasion === 'Holiday' ? 'Please specify the holiday' : 'Please specify the occasion';
          isValid = false;
        }
      });

      // Check for duplicate unique occasions
      const selectedUniqueOccasions = events
        .map((event, index) => ({ occasion: event.occasion, index }))
        .filter(item => UNIQUE_OCCASIONS.includes(item.occasion) && item.occasion);
      
      const duplicateOccasions = selectedUniqueOccasions.reduce((acc, item) => {
        const existing = acc.find(x => x.occasion === item.occasion);
        if (existing) {
          existing.indexes.push(item.index);
        } else {
          acc.push({ occasion: item.occasion, indexes: [item.index] });
        }
        return acc;
      }, [] as { occasion: string; indexes: number[] }[]);

      duplicateOccasions.forEach(duplicate => {
        if (duplicate.indexes.length > 1) {
          duplicate.indexes.forEach(index => {
            errors[`event-${index}-occasion`] = `${duplicate.occasion} can only be selected once per subscription`;
          });
          isValid = false;
        }
      });
    }

    if (showErrors) {
      setFieldErrors(errors);
      setShowValidationErrors(true);
    }

    return isValid;
  };

  // Simple validation for button enable/disable
  const isFormValid = validateForm();

  const handleNext = () => {
    if (!validateForm(true)) return;

    if (flowType === 'one-off') {
      // Always pass the base occasion and custom occasion separately
      onNext({ occasion, customOccasion, date, recipients, flowType });
    } else {
      onNext({ recipient, events, flowType });
    }
  };

  const handleDisabledButtonClick = () => {
    // Show validation errors when clicking disabled button
    validateForm(true);
  };

  return (
    <div className="max-w-7xl w-full mx-auto">
      <ProgressBar currentStep={1} totalSteps={4} />

      {/* Main Content */}
      <div className="mt-8">
        {/* Page Title */}
        <div className="text-center mb-12">
          <h1 className="page-title text-purple-medium">Event & Recipients Setup</h1>
        </div>

        {flowType === 'one-off' ? (
          // One-off flow: Image + Form Layout
          <div className="grid lg:grid-cols-[400px_1fr] gap-12 items-start">
            {/* Left Column - Gift Image */}
            <div className="flex justify-center lg:justify-start">
              <div className="w-80 h-80 rounded-full overflow-hidden shadow-lg">
                <img src={step1CircularPhoto} alt="Gift giving" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="space-y-8">
              {/* Event Details Section */}
              <div className="grid grid-cols-2 gap-6 ">
                <div className="space-y-3">
                  <label className="block form-label font-semibold">What's the occasion?</label>
                  <CustomDropdown
                    value={occasion}
                    onChange={(selectedOccasion) => {
                      setOccasion(selectedOccasion);

                      // Auto-fill date when occasion is selected and has a known date
                      if (hasKnownDate(selectedOccasion)) {
                        const nextDate = getNextOccasionDate(selectedOccasion);
                        if (nextDate) {
                          setDate(nextDate);
                        }
                      }
                    }}
                    options={OCCASION_OPTIONS}
                    placeholder="Select occasion"
                    className={showValidationErrors && fieldErrors['occasion'] ? 'border-red-500' : ''}
                  />
                  {showValidationErrors && fieldErrors['occasion'] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors['occasion']}</p>
                  )}
                </div>

                <div className="space-y-3">
                  <label className="block form-label font-semibold">Date</label>
                  <input
                    type="date"
                    value={date}
                    min={getMinDate()}
                    onChange={(e) => {
                      const value = e.target.value;
                      setDate(value);
                      // Add/remove has-value class for styling
                      if (value) {
                        e.target.classList.add('has-value');
                      } else {
                        e.target.classList.remove('has-value');
                      }
                    }}
                    onBlur={(e) => {
                      const selectedDate = e.target.value;
                      if (selectedDate && !isValidFutureDate(selectedDate)) {
                        alert(`Please select a date at least ${MIN_DATE_WEEKS_IN_FUTURE} weeks in the future.`);
                        e.target.value = '';
                        e.target.classList.remove('has-value');
                        setDate('');
                      }
                    }}
                    className={`px-5 py-4 rounded-full border border-gray-200 bg-white ${showValidationErrors && fieldErrors['date'] ? 'border-red-500' : ''}`}
                  />
                  {showValidationErrors && fieldErrors['date'] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors['date']}</p>
                  )}
                </div>
              </div>

              {occasion === 'Other' && (
                <div className="space-y-3">
                  <label className="block form-label font-semibold">Specify the occasion</label>
                  <input
                    type="text"
                    placeholder="e.g., Graduation"
                    value={customOccasion}
                    onChange={(e) => setCustomOccasion(e.target.value)}
                    className={`px-4 py-3 rounded-2xl ${showValidationErrors && fieldErrors['customOccasion'] ? 'border-red-500' : ''}`}
                  />
                  {showValidationErrors && fieldErrors['customOccasion'] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors['customOccasion']}</p>
                  )}
                </div>
              )}

              {occasion === 'Holiday' && (
                <div className="space-y-3">
                  <label className="block form-label font-semibold">Specify the holiday</label>
                  <input
                    type="text"
                    placeholder="e.g., Christmas, Hanukkah, Thanksgiving"
                    value={customOccasion}
                    onChange={(e) => setCustomOccasion(e.target.value)}
                    className={`px-4 py-3 rounded-2xl ${showValidationErrors && fieldErrors['customOccasion'] ? 'border-red-500' : ''}`}
                  />
                  {showValidationErrors && fieldErrors['customOccasion'] && (
                    <p className="text-sm text-red-600 mt-1">{fieldErrors['customOccasion']}</p>
                  )}
                </div>
              )}

              {/* Recipients Section */}
              <div className="space-y-8 mt-12">
                {recipients.map((recipient, index) => (
                  <div key={index} className="relative recipient-card shadow-sm p-8 pt-12 figma-gradient-border">
                    {/* Recipient Badge - positioned high above the card */}
                    <div className="recipient-badge">
                      <div className="bg-purple-dark text-white px-6 py-3 rounded-3xl text-base font-bold">
                        Recipient {index + 1}
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                      <div className="space-y-3">
                        <label className="block form-label font-medium">Recipient {index + 1} name</label>
                        <input
                          type="text"
                          value={recipient.name}
                          onChange={(e) => {
                            const updated = recipients.map((r, i) =>
                              i === index ? { ...r, name: e.target.value } : r
                            );
                            setRecipients(updated);
                          }}
                          className={`px-5 py-4 input-pill ${showValidationErrors && fieldErrors[`recipient-${index}-name`] ? 'border-red-500' : ''}`}
                          placeholder="Enter a name"
                        />
                        {showValidationErrors && fieldErrors[`recipient-${index}-name`] && (
                          <p className="text-sm text-red-600 mt-1">{fieldErrors[`recipient-${index}-name`]}</p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <label className="block form-label font-medium">Relationship</label>
                        <CustomDropdown
                          value={recipient.relationship}
                          onChange={(selectedRelationship) => {
                            const updated = recipients.map((r, i) =>
                              i === index ? { ...r, relationship: selectedRelationship } : r
                            );
                            setRecipients(updated);
                          }}
                          options={RELATIONSHIP_OPTIONS}
                          placeholder="Select relationship"
                          className={showValidationErrors && fieldErrors[`recipient-${index}-relationship`] ? 'border-red-500' : ''}
                        />
                        {showValidationErrors && fieldErrors[`recipient-${index}-relationship`] && (
                          <p className="text-sm text-red-600 mt-1">{fieldErrors[`recipient-${index}-relationship`]}</p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <label className="block form-label font-medium">Budget Range</label>
                        <div className="grid grid-cols-2 gap-2">
                          <div>
                            <input
                              type="number"
                              min="0"
                              value={recipient.budgetMin || ''}
                              onChange={(e) => {
                                const updated = recipients.map((r, i) =>
                                  i === index ? { ...r, budgetMin: parseInt(e.target.value) || 0 } : r
                                );
                                setRecipients(updated);
                              }}
                              placeholder="50"
                              className={`px-3 py-3 input-pill ${showValidationErrors && fieldErrors[`recipient-${index}-budgetMin`] ? 'border-red-500' : ''}`}
                            />
                            {showValidationErrors && fieldErrors[`recipient-${index}-budgetMin`] && (
                              <p className="text-sm text-red-600 mt-1">{fieldErrors[`recipient-${index}-budgetMin`]}</p>
                            )}
                          </div>
                          <div>
                            <input
                              type="number"
                              min="0"
                              value={recipient.budgetMax || ''}
                              onChange={(e) => {
                                const updated = recipients.map((r, i) =>
                                  i === index ? { ...r, budgetMax: parseInt(e.target.value) || 0 } : r
                                );
                                setRecipients(updated);
                              }}
                              placeholder="150"
                              className={`px-3 py-3 input-pill ${showValidationErrors && fieldErrors[`recipient-${index}-budgetMax`] ? 'border-red-500' : ''}`}
                            />
                            {showValidationErrors && fieldErrors[`recipient-${index}-budgetMax`] && (
                              <p className="text-sm text-red-600 mt-1">{fieldErrors[`recipient-${index}-budgetMax`]}</p>
                            )}
                          </div>
                        </div>
                        {recipient.budgetMin > 0 &&
                          recipient.budgetMax > 0 &&
                          recipient.budgetMin > recipient.budgetMax && (
                            <p className="text-xs text-red-600 mt-1">
                              Minimum budget cannot be higher than maximum budget
                            </p>
                          )}
                      </div>
                    </div>

                    {recipient.relationship === 'Other' && (
                      <div className="mt-6 space-y-3">
                        <label className="block form-label font-medium">
                          Specify your relationship with this recipient
                        </label>
                        <input
                          type="text"
                          placeholder="e.g., Cousin, Neighbor, Colleague"
                          value={recipient.customRelationship || ''}
                          onChange={(e) => {
                            const updated = recipients.map((r, i) =>
                              i === index ? { ...r, customRelationship: e.target.value } : r
                            );
                            setRecipients(updated);
                          }}
                          className={`px-5 py-4 input-pill ${showValidationErrors && fieldErrors[`recipient-${index}-customRelationship`] ? 'border-red-500' : ''}`}
                        />
                        {showValidationErrors && fieldErrors[`recipient-${index}-customRelationship`] && (
                          <p className="text-sm text-red-600 mt-1">{fieldErrors[`recipient-${index}-customRelationship`]}</p>
                        )}
                      </div>
                    )}
                    {/* Remove Button - positioned at top-right */}
                    {recipients.length > 1 && (
                      <button
                        className="flex items-center justify-center transition-colors cursor-pointer mt-7 ml-auto"
                        onClick={() => {
                          if (recipients.length > 1) {
                            const updated = recipients.filter((_, i) => i !== index);
                            setRecipients(updated);
                          }
                        }}
                        title="Remove recipient"
                      >
                        <svg width="16" height="18" viewBox="0 0 16 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <path
                            d="M3 18C2.45 18 1.97934 17.8043 1.588 17.413C1.19667 17.0217 1.00067 16.5507 1 16V3C0.71667 3 0.479337 2.904 0.288004 2.712C0.0966702 2.52 0.000670115 2.28267 3.44827e-06 2C-0.000663218 1.71733 0.0953369 1.48 0.288004 1.288C0.48067 1.096 0.718003 1 1 1H5C5 0.716667 5.096 0.479333 5.288 0.288C5.48 0.0966668 5.71734 0.000666667 6 0H10C10.2833 0 10.521 0.0960001 10.713 0.288C10.905 0.48 11.0007 0.717333 11 1H15C15.2833 1 15.521 1.096 15.713 1.288C15.905 1.48 16.0007 1.71733 16 2C15.9993 2.28267 15.9033 2.52033 15.712 2.713C15.5207 2.90567 15.2833 3.00133 15 3V16C15 16.55 14.8043 17.021 14.413 17.413C14.0217 17.805 13.5507 18.0007 13 18H3ZM13 3H3V16H13V3ZM6 14C6.28334 14 6.521 13.904 6.713 13.712C6.905 13.52 7.00067 13.2827 7 13V6C7 5.71667 6.904 5.47933 6.712 5.288C6.52 5.09667 6.28267 5.00067 6 5C5.71734 4.99933 5.48 5.09533 5.288 5.288C5.096 5.48067 5 5.718 5 6V13C5 13.2833 5.096 13.521 5.288 13.713C5.48 13.905 5.71734 14.0007 6 14ZM10 14C10.2833 14 10.521 13.904 10.713 13.712C10.905 13.52 11.0007 13.2827 11 13V6C11 5.71667 10.904 5.47933 10.712 5.288C10.52 5.09667 10.2827 5.00067 10 5C9.71734 4.99933 9.48 5.09533 9.288 5.288C9.096 5.48067 9 5.718 9 6V13C9 13.2833 9.096 13.521 9.288 13.713C9.48 13.905 9.71734 14.0007 10 14Z"
                            fill="#634B69"
                          />
                        </svg>
                        <span className='text-lg text-purple-medium ml-0.5'>Remove</span>
                      </button>
                    )}
                  </div>
                ))}

                {/* Add Another Recipient Section */}
                {recipients.length < MAX_RECIPIENTS && (
                  <div className="flex items-center gap-6">
                    <button
                      className="px-6 py-3 add-recipient-border text-green-medium hover:bg-green-light hover:text-white rounded-super transition-colors cursor-pointer font-medium"
                      onClick={() => {
                        if (recipients.length < MAX_RECIPIENTS) {
                          setRecipients([...recipients, { name: '', relationship: '', budgetMin: 50, budgetMax: 0 }]);
                        }
                      }}
                    >
                      ADD ANOTHER RECIPIENT
                    </button>
                    <p className="text-sm text-purple-light">{`You can add up to ${MAX_RECIPIENTS} recipients`}</p>
                  </div>
                )}
              </div>

              {/* Next Button */}
              <div className="flex justify-end pt-8">
                <button
                  className={`next font-bold py-4 px-8 rounded-super transition-all duration-300 ${
                    isFormValid
                      ? 'bg-green-light hover:bg-green-medium text-white shadow-md hover:shadow-lg cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-pointer opacity-50'
                  }`}
                  onClick={isFormValid ? handleNext : handleDisabledButtonClick}
                >
                  NEXT →
                </button>
              </div>
            </div>
          </div>
        ) : (
          // Subscription flow: Image + Form Layout (matching one-off style)
          <div className="grid lg:grid-cols-[400px_1fr] gap-12 items-start">
            {/* Left Column - Gift Image */}
            <div className="flex justify-center lg:justify-start">
              <div className="w-80 h-80 rounded-full overflow-hidden shadow-lg">
                <img src={step1CircularPhoto} alt="Gift giving" className="w-full h-full object-cover" />
              </div>
            </div>

            {/* Right Column - Form */}
            <div className="space-y-8">
              {/* Recipient Section */}
              <div className="space-y-6">
                <h3 className="text-xl font-semibold text-purple-medium">Who is receiving the gifts?</h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-3">
                    <label className="block form-label font-medium">Recipient name</label>
                    <input
                      type="text"
                      value={recipient.name}
                      onChange={(e) => setRecipient({ ...recipient, name: e.target.value })}
                      className={`px-5 py-4 input-pill ${showValidationErrors && fieldErrors['recipient-name'] ? 'border-red-500' : ''}`}
                      placeholder="Enter a name"
                    />
                    {showValidationErrors && fieldErrors['recipient-name'] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors['recipient-name']}</p>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="block form-label font-medium">Relationship</label>
                    <CustomDropdown
                      value={recipient.relationship}
                      onChange={(selectedRelationship) =>
                        setRecipient({ ...recipient, relationship: selectedRelationship })
                      }
                      options={RELATIONSHIP_OPTIONS}
                      placeholder="Select relationship"
                      className={showValidationErrors && fieldErrors['recipient-relationship'] ? 'border-red-500' : ''}
                    />
                    {showValidationErrors && fieldErrors['recipient-relationship'] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors['recipient-relationship']}</p>
                    )}
                  </div>
                </div>

                {recipient.relationship === 'Other' && (
                  <div className="space-y-3">
                    <label className="block form-label font-medium">
                      Specify your relationship with this recipient
                    </label>
                    <input
                      type="text"
                      placeholder="e.g., Cousin, Neighbor, Colleague"
                      value={recipient.customRelationship || ''}
                      onChange={(e) => setRecipient({ ...recipient, customRelationship: e.target.value })}
                      className={`px-5 py-4 input-pill ${showValidationErrors && fieldErrors['recipient-customRelationship'] ? 'border-red-500' : ''}`}
                    />
                    {showValidationErrors && fieldErrors['recipient-customRelationship'] && (
                      <p className="text-sm text-red-600 mt-1">{fieldErrors['recipient-customRelationship']}</p>
                    )}
                  </div>
                )}
              </div>

              {/* Events Section */}
              <div className="space-y-8">
                {/* <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 mb-6">
                  <p className="text-sm text-purple-700">
                    <span className="font-medium">Note:</span> You must select exactly 6 events. 
                    Some occasions (like Mother's Day and Valentine's Day) can only be selected once per subscription.
                  </p>
                </div> */}
                {events.map((event, index) => (
                  <div key={index} className="relative recipient-card shadow-sm p-8 pt-12">
                    {/* Event Badge */}
                    <div className="recipient-badge">
                      <div className="bg-purple-dark text-white px-6 py-3 rounded-3xl text-sm font-medium">
                        Event {index + 1}
                      </div>
                    </div>

                    {/* Form Fields */}
                    <div className="grid grid-cols-2 gap-6">
                      <div className="space-y-3">
                        <label className="block form-label font-medium">What's the occasion?</label>
                        <CustomDropdown
                          value={event.occasion}
                          onChange={(selectedOccasion) => handleEventChange(index, 'occasion', selectedOccasion)}
                          options={getAvailableOccasionsForEvent(index)}
                          placeholder="Select occasion"
                          className={showValidationErrors && fieldErrors[`event-${index}-occasion`] ? 'border-red-500' : ''}
                        />
                        {showValidationErrors && fieldErrors[`event-${index}-occasion`] && (
                          <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-occasion`]}</p>
                        )}
                      </div>

                      <div className="space-y-3">
                        <label className="block form-label font-medium">Date</label>
                        <input
                          type="date"
                          value={event.date}
                          min={getMinDate()}
                          onChange={(e) => {
                            const value = e.target.value;
                            handleEventChange(index, 'date', value);
                            // Add/remove has-value class for styling
                            if (value) {
                              e.target.classList.add('has-value');
                            } else {
                              e.target.classList.remove('has-value');
                            }
                          }}
                          onBlur={(e) => {
                            const selectedDate = e.target.value;
                            if (selectedDate && !isValidFutureDate(selectedDate)) {
                              alert(`Please select a date at least ${MIN_DATE_WEEKS_IN_FUTURE} weeks in the future.`);
                              e.target.value = '';
                              e.target.classList.remove('has-value');
                              handleEventChange(index, 'date', '');
                            }
                          }}
                          className={`px-5 py-4 rounded-full border border-gray-200 bg-white ${showValidationErrors && fieldErrors[`event-${index}-date`] ? 'border-red-500' : ''}`}
                        />
                        {showValidationErrors && fieldErrors[`event-${index}-date`] && (
                          <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-date`]}</p>
                        )}
                      </div>
                    </div>

                    {event.occasion === 'Other' && (
                      <div className="mt-6 space-y-3">
                        <label className="block form-label font-medium">Specify the occasion</label>
                        <input
                          type="text"
                          placeholder="e.g., Graduation"
                          value={event.customOccasion || ''}
                          onChange={(e) => handleEventChange(index, 'customOccasion', e.target.value)}
                          className={`px-5 py-4 input-pill ${showValidationErrors && fieldErrors[`event-${index}-customOccasion`] ? 'border-red-500' : ''}`}
                        />
                        {showValidationErrors && fieldErrors[`event-${index}-customOccasion`] && (
                          <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-customOccasion`]}</p>
                        )}
                      </div>
                    )}

                    {event.occasion === 'Holiday' && (
                      <div className="mt-6 space-y-3">
                        <label className="block form-label font-medium">Specify the holiday</label>
                        <input
                          type="text"
                          placeholder="e.g., Christmas, Hanukkah, Thanksgiving"
                          value={event.customOccasion || ''}
                          onChange={(e) => handleEventChange(index, 'customOccasion', e.target.value)}
                          className={`px-5 py-4 input-pill ${showValidationErrors && fieldErrors[`event-${index}-customOccasion`] ? 'border-red-500' : ''}`}
                        />
                        {showValidationErrors && fieldErrors[`event-${index}-customOccasion`] && (
                          <p className="text-sm text-red-600 mt-1">{fieldErrors[`event-${index}-customOccasion`]}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Next Button */}
              <div className="flex justify-end pt-8">
                <button
                  className={`font-bold py-4 px-8 rounded-super transition-all duration-300 ${
                    isFormValid
                      ? 'bg-green-light hover:bg-green-medium text-white shadow-md hover:shadow-lg cursor-pointer'
                      : 'bg-gray-300 text-gray-500 cursor-pointer opacity-50'
                  }`}
                  onClick={isFormValid ? handleNext : handleDisabledButtonClick}
                >
                  NEXT →
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
